import type { AIModel } from '@volt/shared';

export const AUTO_MODEL_CHOICE_ID = '__auto__';
export const QUICK_MODELS_PREF_KEY = 'chatQuickModels.v1';

export type ModelDifficulty = 'fast' | 'normal' | 'complex' | 'expert';

/** `with` = models that can search the web, `without` = models that answer from training data only. */
export type InternetFilter = 'all' | 'with' | 'without';

export interface ModelFilters {
  query: string;
  provider: 'all' | 'anthropic' | 'xai' | 'google' | 'openai' | 'moonshot' | 'deepseek' | 'microsoft';
  internet: InternetFilter;
  difficulty: 'all' | ModelDifficulty;
}

export const DEFAULT_MODEL_FILTERS: ModelFilters = {
  query: '',
  provider: 'all',
  internet: 'all',
  difficulty: 'all',
};

export function hasActiveModelFilters(filters: ModelFilters): boolean {
  return (
    filters.query.trim().length > 0 ||
    filters.provider !== DEFAULT_MODEL_FILTERS.provider ||
    filters.internet !== DEFAULT_MODEL_FILTERS.internet ||
    filters.difficulty !== DEFAULT_MODEL_FILTERS.difficulty
  );
}

export function clampModelPickerAnchorX(
  anchorX: number,
  viewportWidth: number,
  panelMaxWidth: number,
  viewportGap: number,
): number {
  const clampedGap = Math.max(0, viewportGap);
  const availableWidth = Math.max(0, viewportWidth - clampedGap * 2);
  const panelWidth = Math.min(panelMaxWidth, availableWidth);
  const maxLeft = Math.max(clampedGap, viewportWidth - panelWidth - clampedGap);
  return Math.min(maxLeft, Math.max(clampedGap, anchorX));
}

export function modelRecencyScore(model: AIModel): number {
  const source = `${model.displayName} ${model.modelCode}`.toLowerCase();
  const numbers = Array.from(source.matchAll(/(\d+(?:\.\d+)?)/g))
    .map((m) => Number(m[1]))
    .filter((value) => Number.isFinite(value));
  const base = numbers.length > 0 ? Math.max(...numbers) : 0;
  const tinyPenalty = /mini|lite|small/.test(source) ? -0.1 : 0;
  return base + tinyPenalty;
}

function pickLatest(
  models: AIModel[],
  match: (model: AIModel) => boolean,
): AIModel | undefined {
  const matched = models.filter(match);
  if (matched.length === 0) return undefined;
  return [...matched].sort((a, b) => modelRecencyScore(b) - modelRecencyScore(a))[0];
}

function isVisibleChatModel(model: AIModel): boolean {
  return (
    model.section === 'chat' &&
    model.isActive &&
    model.availabilityStatus !== 'pending_review' &&
    model.availabilityStatus !== 'retired'
  );
}

export function availableChatModels(models: AIModel[]): AIModel[] {
  return models.filter(
    (model) =>
      isVisibleChatModel(model) &&
      model.isAvailable &&
      model.availabilityStatus !== 'unavailable',
  );
}

export function getDefaultQuickModelIds(models: AIModel[]): string[] {
  const chatModels = availableChatModels(models);
  const picks = [
    pickLatest(chatModels, (model) => model.providerCode === 'anthropic' && /haiku/i.test(model.displayName)),
    pickLatest(chatModels, (model) => model.providerCode === 'anthropic' && /sonnet/i.test(model.displayName)),
    pickLatest(chatModels, (model) => model.providerCode === 'xai'),
    pickLatest(chatModels, (model) => model.providerCode === 'google' && /flash/i.test(model.displayName)),
    pickLatest(chatModels, (model) => model.providerCode === 'openai' && /gpt/i.test(model.displayName)),
    pickLatest(chatModels, (model) => model.providerCode === 'deepseek'),
    pickLatest(chatModels, (model) => model.providerCode === 'moonshot' || /kimi/i.test(model.displayName)),
  ];

  return [
    AUTO_MODEL_CHOICE_ID,
    ...picks.map((model) => model?.id).filter((value): value is string => Boolean(value)),
  ];
}

export function sanitizeQuickModelIds(modelIds: string[], models: AIModel[]): string[] {
  const availableIds = new Set(availableChatModels(models).map((model) => model.id));
  const next: string[] = [];
  for (const modelId of modelIds) {
    if (modelId === AUTO_MODEL_CHOICE_ID || availableIds.has(modelId)) {
      if (!next.includes(modelId)) next.push(modelId);
    }
  }
  return next.length > 0 ? next : getDefaultQuickModelIds(models);
}

export function resolveAutoModelId(models: AIModel[]): string | undefined {
  const chatModels = availableChatModels(models);
  return (
    pickLatest(chatModels, (model) => model.providerCode === 'anthropic' && /sonnet/i.test(model.displayName))
      ?.id ??
    pickLatest(chatModels, (model) => model.providerCode === 'openai' && /gpt/i.test(model.displayName))?.id ??
    chatModels[0]?.id
  );
}

export function modelDifficulty(model: AIModel): ModelDifficulty {
  const name = `${model.displayName} ${model.modelCode}`.toLowerCase();
  if (/opus|reason|r1/.test(name)) return 'expert';
  if (/pro|sonnet|grok 4|gpt-4\.1|deepseek v3|k2|mai-1/.test(name)) return 'complex';
  if (/mini|flash|haiku|nano/.test(name)) return 'fast';
  return 'normal';
}

/** Provider codes that currently have at least one working chat model (drives the provider chips). */
export function availableProviderCodes(models: AIModel[]): Set<string> {
  return new Set(availableChatModels(models).map((model) => model.providerCode));
}

export function filterModels(models: AIModel[], filters: ModelFilters): AIModel[] {
  const search = filters.query.trim().toLowerCase();
  // Strict: only models the server has verified as working are listed; broken ones are not shown at all.
  return availableChatModels(models)
    .filter((model) => {
      if (filters.provider !== 'all' && model.providerCode !== filters.provider) return false;
      if (filters.internet === 'with' && !model.supportsWebSearch) return false;
      if (filters.internet === 'without' && model.supportsWebSearch) return false;
      if (filters.difficulty !== 'all' && modelDifficulty(model) !== filters.difficulty) return false;
      if (!search) return true;
      return (
        model.displayName.toLowerCase().includes(search) ||
        model.modelCode.toLowerCase().includes(search) ||
        model.providerCode.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function providerLabel(providerCode: string): string {
  switch (providerCode) {
    case 'anthropic':
      return 'Claude';
    case 'xai':
      return 'Grok';
    case 'google':
      return 'Gemini';
    case 'openai':
      return 'GPT';
    case 'moonshot':
      return 'Kimi';
    case 'deepseek':
      return 'DeepSeek';
    case 'microsoft':
      return 'Microsoft AI';
    default:
      return providerCode;
  }
}
